import ButtonComponent from '../../button/buttonComponent';
import {
  ArrayPath,
  FieldArray,
  FieldValues,
  Path,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { Question } from '../hooks/useQuestionnaire';
import { DynamicQuestionnaireDependencie } from '../../dynamicQuestionnaire/interfaces/dynamicQuestionnaireResponse.interface';
import {
  CustomFieldProps,
  QuestionnaireField,
  UIControl,
} from '../../stepper/field-components/QuestionnaireField';
import { NestedDependentsRenderer } from '../../stepper/field-components/NestedQuestionnaireFields';
import React, { useMemo } from 'react';
import { ReactComponent as MinusIcon } from '../../../assets/icons/minusIcon.svg';
import { ReactComponent as Plus } from 'assets/icons/plusIcon.svg';

type ArrayFieldProps = {
  question: Question;
  disabled?: boolean;
  dynamicDeps: {
    requiredFields: string[];
    disabledFields: string[];
  };
  dependencies: DynamicQuestionnaireDependencie[];
  questionsById: Record<string, Question>;
  customFields?: Partial<Record<string, React.ComponentType<CustomFieldProps>>>;
};

export const ArrayQuestionnaireField = <TFormValues extends FieldValues>({
  question,
  dynamicDeps,
  dependencies,
  questionsById,
  customFields,
  disabled = false,
}: ArrayFieldProps) => {
  const { control } = useFormContext<TFormValues>();

  const arrayName = String(question.id) as ArrayPath<TFormValues>;
  const childrenQuestions: Question[] = (question as Question)?.children ?? [];

  const value = useWatch({ control, name: arrayName as Path<TFormValues> });
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: arrayName,
  });

  const childIds = childrenQuestions.map((child) => {
    const childId = String(child.id);
    return childId;
  });

  const maxValue = useMemo(() => {
    return question.array_field_config?.max_items || 10;
  }, [question.array_field_config?.max_items]);

  const isArray = React.useMemo(() => Array.isArray(value), [value]);

  const emptyItem = useMemo(() => {
    return childIds.reduce((acc, id) => {
      acc[id] = '';
      return acc;
    }, {} as Record<string, string>);
  }, [childIds]);

  React.useEffect(() => {
    if (!isArray) {
      replace([emptyItem] as FieldArray<TFormValues, ArrayPath<TFormValues>>[]);
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArray, fields.length, replace]);

  return (
    <div>
      {fields.map((field, idx) => {
        return (
          <div
            key={field.id}
            className="flex flex-col desktop:flex-row gap-2.5"
          >
            {childrenQuestions.map((child) => {
              const childId = String(child.id);
              const childName =
                `${arrayName}.${idx}.${childId}` as Path<TFormValues>;
              const isRequired = dynamicDeps?.requiredFields?.includes(childId);
              const isDisabled =
                dynamicDeps.disabledFields.includes(childId) || disabled;
              const CustomFieldCmp = customFields?.[childId];

              if (CustomFieldCmp) {
                return (
                  <div key={childId} className="mb-3 flex-grow-0">
                    <CustomFieldCmp
                      question={child}
                      dependencies={dependencies}
                      isRequired={isRequired}
                      isDisabled={isDisabled}
                      questionsById={questionsById}
                    />
                  </div>
                );
              }

              return (
                <div className="mb-3 flex-grow-0 flex-shrink-0">
                  <QuestionnaireField<TFormValues>
                    name={childName}
                    label={child.text as string}
                    ui={child.ui_control as UIControl}
                    answers={child.answers ?? []}
                    placeholder={child.helper_text}
                    required={isRequired}
                    disabled={isDisabled}
                    subtitle={child.subtitle}
                  />
                  <NestedDependentsRenderer
                    parentQuestion={child}
                    dependencies={dependencies}
                    questionsById={questionsById}
                    customFields={customFields}
                  />
                </div>
              );
            })}

            <div className="flex gap-2">
              {fields.length > 1 && (
                <ButtonComponent
                  Icon={MinusIcon}
                  type="icon"
                  paddingX="px-2.5"
                  paddingY="py-2.5"
                  testID="remove-array-field-btn"
                  className="mt-30"
                  onClick={() => remove(idx)}
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        );
      })}

      <ButtonComponent
        testID="add-array-field-btn"
        onClick={() =>
          append(emptyItem as FieldArray<TFormValues, ArrayPath<TFormValues>>)
        }
        type="underline"
        Icon={Plus}
        iconPosition="left"
        htmlButtonType="button"
        disabled={disabled || fields.length > maxValue}
      >
        {question.array_field_config?.add_button_label ?? 'Add'}
      </ButtonComponent>
    </div>
  );
};
